/**
 * <hero-slideshow>
 *
 * Companion element for sections/hero-slideshow.liquid.
 *
 * assets/slideshow.js handles scroll-snap, drag, autoplay, infinite loop, keyboard and ARIA
 * for the main slideshow. It has zero <video> awareness, so this element owns:
 *
 *   1. Video lifecycle — play only the active slide, and defer source loading until first
 *      activation. Without this, every video slide downloads and decodes at once, forever.
 *      (Deferral pattern from assets/video-background.js; play/pause precedent at
 *      assets/layered-slideshow.js:309.)
 *   2. Reduced motion — slideshow.js consults prefersReducedMotion() only for scroll
 *      smoothness, never for autoplay, so rotation is paused here explicitly.
 *   3. The stats strip below the hero is a pure-CSS marquee — no JS involved.
 *
 * Deliberately import-free: a plain HTMLElement that cannot break if the importmap changes.
 */

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const MOBILE = matchMedia('(max-width: 749px)');

class HeroSlideshow extends HTMLElement {
  #slides = [];
  #rotationTimer = null;
  #rotationMs = 0;
  #pausedObserver = null;

  connectedCallback() {
    this.slideshow = this.querySelector('slideshow-component');
    this.#slides = Array.from(this.querySelectorAll('slideshow-slide'));

    this.addEventListener('slideshow:select', this.#onSelect);

    // slideshow.js emits no event for the initial slide, so prime it manually.
    this.#sync(0);

    if (REDUCED.matches) {
      this.#pauseRotation();
    } else {
      // slideshow-component may not have upgraded yet, and its own setup registers the
      // hover listeners we need to remove — so wait for both before taking over.
      customElements.whenDefined('slideshow-component').then(() => {
        requestAnimationFrame(() => this.#takeOverRotation());
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('slideshow:select', this.#onSelect);
    this.#pausedObserver?.disconnect();
    this.#pausedObserver = null;
    this.#stopRotation();
  }

  /* ── Rotation ────────────────────────────────────── */

  /**
   * Rotation is driven here rather than by slideshow.js, which pauses it in two ways that
   * are wrong for a full-viewport hero:
   *
   *   1. Hover. It suspends on `mouseenter` (slideshow.js:556) and re-checks `:hover`
   *      inside its own timer (slideshow.js:349). Correct for a small product carousel;
   *      wrong at --hsl-height: 100svh, where the cursor sits over the section for most
   *      of the time a visitor spends at the top of the page, so slides never advance.
   *   2. Drag. It calls pause() on drag start (slideshow.js:732), which latches a
   *      `paused` attribute that its own resume() then refuses to clear — `resume()`
   *      returns early while `paused` is set, and only play() clears it. So one swipe
   *      stopped rotation permanently until reload.
   *
   * Owning the timer here sidesteps both without changing behaviour for other carousels.
   */
  #takeOverRotation() {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    const seconds = parseInt(slideshow.getAttribute('autoplay') ?? '', 10);
    if (Number.isNaN(seconds) || seconds <= 0) return; // Auto-rotate switched off in the editor.

    // `suspend`/`resume` are prototype methods, so these are the same references
    // slideshow.js registered and removeEventListener matches them.
    slideshow.removeEventListener('mouseenter', slideshow.suspend);
    slideshow.removeEventListener('mouseleave', slideshow.resume);
    slideshow.suspend?.();

    this.#rotationMs = seconds * 1000;

    /* The play/pause control calls the engine's own play()/pause() directly
       (snippets/slideshow-controls.liquid: on:click="/play" and "/pause"), and the icon
       state is driven by the [paused] attribute those set. Since the timer now lives here,
       that button has to drive this timer instead of the one it thinks it owns — follow
       the attribute. Without this, pause left our interval running (slides advancing under
       a "paused" icon) while play started the engine's interval alongside ours, so the two
       fired against each other. */
    this.#pausedObserver = new MutationObserver(() => {
      if (slideshow.hasAttribute('paused')) {
        this.#stopRotation();
      } else {
        // play() has just started the engine's timer; we own the cadence, so drop it.
        slideshow.suspend?.();
        this.#restartRotation();
      }
    });
    this.#pausedObserver.observe(slideshow, { attributes: true, attributeFilter: ['paused'] });

    this.#restartRotation();
  }

  /** Restarted on every selection so a manual arrow tap gets a full dwell, not a stub. */
  #restartRotation() {
    if (!this.#rotationMs) return;
    // A deliberate pause outranks selection: arrowing through slides while paused must
    // not quietly resume rotation.
    if (this.slideshow?.hasAttribute('paused')) return;
    this.#stopRotation();
    this.#rotationTimer = setInterval(() => {
      if (document.hidden) return;
      this.slideshow?.next?.();
    }, this.#rotationMs);
  }

  #stopRotation() {
    if (this.#rotationTimer) clearInterval(this.#rotationTimer);
    this.#rotationTimer = null;
  }

  /* ── Main slideshow: video lifecycle ─────────────── */

  #onSelect = (event) => {
    const index = event?.detail?.index;
    if (typeof index === 'number') this.#sync(index);
    this.#restartRotation();
  };

  /** slideshow-component may not have upgraded yet when we connect. */
  #pauseRotation() {
    const stop = () => {
      try {
        this.slideshow?.pause?.();
      } catch {
        /* best-effort; a failure here must not break the slideshow */
      }
    };

    if (this.slideshow && !customElements.get('slideshow-component')) {
      customElements.whenDefined('slideshow-component').then(stop);
    } else {
      stop();
    }
  }

  #sync(activeIndex) {
    // CSS hides video on mobile and under reduced motion — never spend the bytes.
    if (REDUCED.matches || MOBILE.matches) return;

    this.#slides.forEach((slide, i) => {
      const video = slide.querySelector('[data-hsl-video]');
      if (!video) return;

      if (i === activeIndex) {
        this.#attach(video);
        // Autoplay rejection (no gesture, low power mode) is expected and non-fatal;
        // the poster image stays visible underneath.
        video.play?.().catch(() => {});
      } else {
        video.pause?.();
      }
    });
  }

  /** Copy data-hsl-src -> src on first activation, then load. */
  #attach(video) {
    if (video.dataset.hslLoaded) return;
    video.dataset.hslLoaded = '1';

    let attached = false;
    for (const source of video.querySelectorAll('source[data-hsl-src]')) {
      source.src = source.dataset.hslSrc;
      attached = true;
    }
    if (attached) video.load();
  }

}

if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}
